import React, { useCallback, useEffect, useRef } from 'react';
import css from './CalendarSelectTime.module.scss';
import { Flexbox, IView } from '@framework-controls/core';
import { Slider } from '@framework-controls/slider';
import CalendarSelectTimeBtn from './CalendarSelectTimeBtn';
import { strings } from 'src/localization';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import { ComponentMapIds } from 'src/components/map';
import { ascCtaInteraction, ascPageview, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import { TimeOfDay, ShopHour, ascPageviewInput, ascCtaInteractionInput, VehicleRec } from 'src/models';
import classNames from 'classnames';

interface TimeMark {
    label: string;
    value: number;
}
const timeMarks: TimeMark[] = [
    { label: '2', value: 200 },
    { label: '3', value: 300 },
    { label: '4', value: 400 },
    { label: '5', value: 500 },
    { label: '6', value: 600 },
    { label: '7', value: 700 },
    { label: '8', value: 800 },
    { label: '9', value: 900 },
    { label: '10', value: 1000 },
    { label: '11', value: 1100 },
    { label: '12', value: 1200 },
    { label: '1', value: 1300 },
    { label: '2', value: 1400 },
    { label: '3', value: 1500 },
    { label: '4', value: 1600 },
    { label: '5', value: 1700 },
    { label: '6', value: 1800 },
    { label: '7', value: 1900 },
    { label: '8', value: 2000 },
    { label: '9', value: 2100 },
];

interface IProps {
    id: string;
    timeOfDay: TimeOfDay[];
    selectedTOD: TimeOfDay;
    shopHours?: ShopHour;
    theme: string;
    colorSensitiveEnabled: boolean;
    mainPaneViewOrder: IView[];
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    timeSelect: (timeRange: TimeOfDay) => void;
}

const CalendarSelectTime = (props: IProps) => {
    const defaultStart = parseInt(props.selectedTOD.startTime, 10);
    const defaultEnd = parseInt(props.selectedTOD.endTime, 10);
    const defaultTitle = props.selectedTOD.title;
    const [selectedTime, setSelectedTime] = React.useState(defaultTitle);
    const [timeRange, setTimeRange] = React.useState<TimeOfDay>(props.selectedTOD);
    const [timeVal, setTimeVal] = React.useState([defaultStart, defaultEnd]);
    const timeValRef = useRef(timeVal);
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;

    const timeHandler = useCallback((time: TimeOfDay, e: any) => {
        setSelectedTime(time.title);
        setTimeRange(time);
        const startTime = parseInt(time.startTime, 10);
        const endTime = parseInt(time.endTime, 10);
        setTimeVal([startTime, endTime]);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: time.title,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.selectedVehicle, props.vehicles]);

    const timeRangeHandler = useCallback((id: string, value: number | number[]) => {
        setTimeVal([value[0], value[1]]);
        setSelectedTime(id);
        const time: TimeOfDay = {
            startTime: value[0],
            endTime: value[1],
            title: strings.CustomRange,
            titleEN: strings.CustomRange,
            titleFR: strings.CustomRange,
            default: false,
        };
        setTimeRange(time);
        // TO DO
    }, []);

    const updateHandler = useCallback((e: any) => {
        props.timeSelect(timeRange);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Update,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [timeRange, props.timeSelect, props.selectedVehicle, props.vehicles]);

    const shopHoursOpen1 = props.shopHours?.open1 ?? '';
    const shopHoursOpen2 = props.shopHours?.open2 ?? '';
    const shopHoursOpen3 = props.shopHours?.open3 ?? '';

    // let min = props.shopHours !== undefined ? parseInt(props.shopHours.open1, 10) : defaultStart;
    let min = props.shopHours !== undefined ? parseInt(shopHoursOpen1, 10) : defaultStart;
    // we only work with whole hours for the starting point for the timeRange slider
    const minTest = Math.floor(min / 100);
    min = minTest * 100;

    if (props.shopHours &&
    // props.shopHours.open1 === '' &&
    // props.shopHours.open2 === '' &&
    // props.shopHours.open3 === '') {
        shopHoursOpen1 === '' &&
        shopHoursOpen2 === '' &&
        shopHoursOpen3 === '') {
        min = defaultStart;
    }
    let closeHours = defaultEnd;
    if (props.shopHours?.close1) {
        closeHours = parseInt(props.shopHours.close1);
    }
    if (props.shopHours?.close2) {
        closeHours = parseInt(props.shopHours.close2);
    }
    if (props.shopHours?.close3) {
        closeHours = parseInt(props.shopHours.close3);
    }

    // const max = props.shopHours !== undefined ? parseInt(props.shopHours.close1, 10) : 2100;
    const max = closeHours;
    const marks = props.shopHours !== undefined ? GetTimeMarks(props.shopHours, defaultStart, defaultEnd) : timeMarks;
    // if (props.shopHours &&
    //     props.shopHours.open1 === '' &&
    //     props.shopHours.open2 === '' &&
    //     props.shopHours.open3 === '') {
    //     marks = timeMarks;
    // }

    // if timeOfDay has a morning time less than the shop hours equalize it to match custom slider
    // same for the evening.
    const timeOfDayCopy: TimeOfDay[] = [];
    props.timeOfDay.forEach((time) => {
        timeOfDayCopy.push({
            title: time.title,
            titleEN: time.titleEN,
            titleFR: time.titleFR,
            startTime: time.startTime,
            endTime: time.endTime,
            default: time.default,
        });
    });

    const sliderEventHandler = useCallback((e: any) => {
        const elementColor = getElementColor(e, 'color');
        let eventActionResult = '';
        if ((timeValRef.current[1] - timeValRef.current[0]) > (timeVal[1] - timeVal[0])) {
            eventActionResult = 'decrease';
        } else if ((timeValRef.current[1] - timeValRef.current[0]) < (timeVal[1] - timeVal[0])) {
            eventActionResult = 'increase';
        }
        timeValRef.current = timeVal;
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.CustomRange,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: eventActionResult,
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle, timeVal]);

    // filter the timeOfDayCopy array to exclude examples that don't fit with the min-max range
    // const filteredTimeOfDay = timeOfDayCopy.filter((x) => (parseInt(x.startTime) <= max && parseInt(x.endTime) >= min));
    const filteredTimeOfDay = timeOfDayCopy;
    // clip extreme ends of buttons if they go outside the slider hours
    filteredTimeOfDay.forEach((timeSlot) => {
        if (parseInt(timeSlot.startTime) < min) {
            timeSlot.startTime = min+'';
        }
    });
    filteredTimeOfDay.forEach((timeSlot) => {
        if (parseInt(timeSlot.endTime) > max) {
            timeSlot.endTime = max+'';
        }
    });

    const timeBtns =
        filteredTimeOfDay.map((time: TimeOfDay, index) => (
            // <div id={time.title} key={index}>
            <CalendarSelectTimeBtn
                id={time.title}
                key={index}
                timeRange={time}
                onClick={timeHandler}
                selected={selectedTime === time.title}
                theme={props.theme}
                colorSensitiveEnabled={props.colorSensitiveEnabled}
            />
            // </div>
        ));

    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CalendarSelect) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle]);

    const customRangeClassNames = classNames(
        css.customRange,
        {
            [css.customRangeCV]: props.theme === 'chevrolet',
            [css.customRangeHonda]: props.theme === 'honda',
            [css.customRangeBuick]: props.theme === 'buick',
            [css.customRangeBMW]: props.theme === 'bmw',
            [css.customRangeFord]: props.theme === 'ford',
            [css.customRangeIN]: props.theme === 'infiniti',
            [css.customRangeNissan]: props.theme === 'nissan' || props.theme === 'mercedesBenz',
            [css.customRangeMazda]: props.theme === 'mazda',
            [css.customRangeAcura]: props.theme === 'acura',
            [css.customRangeGenesis]: props.theme === 'genesis',
            [css.customRangeLexus]: props.theme === 'lexus',
        },
    );

    let barID = '';
    switch (props.theme){
        case 'buick':
            barID = 'barBuick';
            break;
        case 'colorSensitive':
            barID = 'barADA';
            break;
        case 'infiniti':
            barID = 'barIN';
            break;
        default:
            barID = 'bar';
            break;
    }
    if (props.colorSensitiveEnabled) {
        barID = 'barADA';
    }

    return (
        <div className={css.timeMenu}>
            <Flexbox
                container
                spacing={0}
                direction={'row'}
                noMargin={false}
                alignItems={'center'}
                justify={'space-around'}
            >
                {timeBtns}
            </Flexbox>
            {
                parseInt(timeRange.startTime) >= parseInt(timeRange.endTime) ?
                    <div className={css.noTimes}>
                        {strings.NoTimesInTimeRange}
                    </div> :
                    <div>
                        <div className={customRangeClassNames}>
                            <hr id={barID} />
                            <div >
                                {strings.CustomRange}
                            </div>
                        </div>
                        <div onMouseUpCapture={sliderEventHandler}>
                            <Slider
                                id='slider'
                                min={min}
                                max={max}
                                step={100}
                                width='fullWidth'
                                marks={marks}
                                value={timeVal}
                                onChange={timeRangeHandler}
                            />
                        </div>
                        <div>
                            <SVPButton
                                id='updateBtn'
                                message={strings.Update}
                                variant='secondaryBtnAlt'
                                handler={updateHandler}
                                tooltip={strings.UpdateTitle}
                                theme={props.theme}
                                noArrow={noArrow}
                            />
                        </div>
                    </div>
            }
        </div>
    );
};

function GetTimeMarks(shopHours: ShopHour, defaultMin: number, defaultMax: number): TimeMark[] {
    const marks: TimeMark[] = [];
    let workingDefaultMin = defaultMin;
    workingDefaultMin = Math.floor(workingDefaultMin / 100);
    workingDefaultMin = workingDefaultMin * 100;
    let minTime = workingDefaultMin;
    if (shopHours.open1) {
        minTime = parseInt(shopHours.open1, 10);
        // We only work with whole hours for the starting point of the TimeRange slider
        const minTest = Math.floor(minTime / 100);
        minTime = minTest * 100;
    }
    // const maxTime = parseInt(shopHours.close1, 10);
    let maxTime = defaultMax;
    if (shopHours?.close1) {
        maxTime = parseInt(shopHours.close1, 10);
    }
    if (shopHours?.close2) {
        maxTime = parseInt(shopHours.close2, 10);
    }
    if (shopHours?.close3) {
        maxTime = parseInt(shopHours.close3, 10);
    }



    for (let step = minTime; step <= maxTime; step = step + 100) {
        const hours = step / 100;
        const label = hours > 12 ? hours - 12 : hours;
        const mark: TimeMark = {
            value: step,
            label: label.toString(),
        };
        marks.push(mark);
    }

    return marks;
}

export default CalendarSelectTime;